import { forwardRef, type ReactNode } from 'react'
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio as MuiRadio,
  type FormControlLabelProps,
  type FormControlProps,
  type RadioProps as MuiRadioProps,
} from '@mui/material'

export type RadioShape = 'default' | 'rounded'

export type RadioProps = Omit<MuiRadioProps, 'children'> & {
  error?: boolean
  formControlLabelProps?: Omit<FormControlLabelProps, 'control' | 'disabled' | 'label' | 'required'>
  formControlProps?: Omit<FormControlProps, 'disabled' | 'error' | 'required'>
  helperText?: ReactNode
  label?: ReactNode
  shape?: RadioShape
}

type RadioIndicatorProps = {
  shape: RadioShape
  state: 'unchecked' | 'checked'
}

function RadioIndicator({ shape, state }: RadioIndicatorProps) {
  return (
    <span aria-hidden className="CodesignRadioIndicator" data-shape={shape} data-state={state}>
      {state === 'checked' ? (
        <svg viewBox="-4 -4 8 8">
          <circle cx="0" cy="0" fill="#ffffff" r="2" />
        </svg>
      ) : null}
    </span>
  )
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { checkedIcon, error = false, formControlLabelProps, formControlProps, helperText, icon, label, shape = 'default', slotProps, ...props },
  ref,
) {
  const inputSlotProps = slotProps?.input

  const control = (
    <MuiRadio
      {...props}
      checkedIcon={checkedIcon ?? <RadioIndicator shape={shape} state="checked" />}
      icon={icon ?? <RadioIndicator shape={shape} state="unchecked" />}
      slotProps={{
        ...slotProps,
        input:
          typeof inputSlotProps === 'function'
            ? ownerState => {
                const resolved = inputSlotProps(ownerState)

                return {
                  ...resolved,
                  ref,
                }
              }
            : {
                ...(slotProps?.input ?? {}),
                ref,
              },
      }}
    />
  )

  if (!label && !helperText) {
    return control
  }

  return (
    <FormControl {...formControlProps} disabled={props.disabled} error={error} required={props.required}>
      {label ? <FormControlLabel {...formControlLabelProps} control={control} label={label} /> : control}
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  )
})
